import { GetAvailableStatusesForMoveResponse, GetTrackersResponse, NextAvailableStatus } from '@/api/project/response';
import { Permission } from '@/features/permissions';
import { ClientService } from '@/types/api';
import { CheckProjectsSimilarityResponse } from "@/api/businessProcesses/response";
import { BusinessProcessDetailsDTO } from "@/features/admin/businessProcess/dtos/businessProcess";
import { IStatus } from "@/types/projects";
import { AxiosResponse } from 'axios';
import { InvitationRoleTypeEnum } from '@/types/invitation-role-type-enum';
import { IMoveProjectToNextStatusDto } from '@/api/project/request';
import { ProjectAdditionalStatusEnum } from '@/types/project-detail';

export class ProjectApi {
	private prefix = 'projects';

	constructor(private readonly clientService: ClientService) {
		this.clientService = clientService;
	}

	public async getTrackers() {
		const defaultTrackers: GetTrackersResponse = [];
		return Permission.instance.canNotAccess('projectUpdateTracker')
			? { data: defaultTrackers }
			: this.clientService.get(`${this.prefix}/trackers`);
	}

    public async updateTags(applicationId: number, ids: number[]): Promise<AxiosResponse<number>> {
		return await this.clientService.put(`${this.prefix}/${applicationId}/tags`, {competencyIds: ids});
	}

	public async revertStatus(projectId: number, statusLogId: number): Promise<AxiosResponse<number>> {
		return await this.clientService.post(`${this.prefix}/status/revert-to-history/${projectId}/${statusLogId}`);
	}

	public async getAvailableStatusesForMove(dto: { projectIds: number[] }): Promise<AxiosResponse<GetAvailableStatusesForMoveResponse>> {
        if (!dto?.projectIds) {
            return;
        }
		return await this.clientService.post<GetAvailableStatusesForMoveResponse>(`${this.prefix}/get-next-available-statuses/`, dto);
	}

	public async moveToNextStatus(dto: IMoveProjectToNextStatusDto): Promise<AxiosResponse<boolean>> {
		return await this.clientService.postForm(`${this.prefix}/move-to-next-status/`, dto);
    }

	public async confirmParticipants(projectId: number): Promise<AxiosResponse<boolean>> {
		return await this.clientService.post(`${this.prefix}/${projectId}/confirm-participants/`);
	}

	public async selectEvaluationMethod(projectId: number, method: 'simplified' | 'standard'): Promise<AxiosResponse<boolean>> {
		if (!method) {
            return;
        }
        return await this.clientService.post(`${this.prefix}/${projectId}/select-evaluation-method/${method}`);
	}

	public async setProjectAdditionalStatus(projectId: number, status: ProjectAdditionalStatusEnum | null): Promise<AxiosResponse<boolean>> {
		return await this.clientService.patch(`${this.prefix}/${projectId}/additional-status/`, {
            status
        });
	}

	public async setProjectRecommendation(projectId: number, dto: { recommendation: 'support' | 'reject' | null, comment: string }): Promise<AxiosResponse<boolean>> {
		return await this.clientService.patch(`${this.prefix}/${projectId}/recommendation/`, dto);
	}

	public async kickProjectParticipant(projectId: number, userId: number, role: InvitationRoleTypeEnum): Promise<AxiosResponse<boolean>> {
		if (!userId || !role) {
            return;
        }

        return await this.clientService.delete(`${this.prefix}/${projectId}/kick-participant`, {
            params: {
                userId,
                role
            }
        });
	}

	public async setIAmLeader(projectId: number): Promise<AxiosResponse<boolean>> {
        return await this.clientService.post(`${this.prefix}/${projectId}/i-am-leader`);
	}
}
